import type { ComponentProps } from '@enonic/react-components'
import * as React from 'react'
import { Section, Container, ResponsiveImage } from '../primitives'
import { Icon } from '/react4xp/components/primitives/Icon'
import type { ResponsiveImageData } from '../primitives'

interface ContactPerson {
  id: string
  name: string
  jobTitle?: string
  email?: string
  phone?: string
  image?: ResponsiveImageData
}

export interface ContactsData extends Record<string, unknown> {
  heading: string
  backgroundColor: 'none' | 'light-blue' | 'light-peach'
  persons: ContactPerson[]
  linkUrl?: string
  linkLabel?: string
  linkNewTab: boolean
}

const bgMap: Record<ContactsData['backgroundColor'], string> = {
  none: '',
  'light-blue': 'bg-slate-100',
  'light-peach': 'bg-peach-100',
}

export const Contacts = (props: ComponentProps) => {
  const { heading, backgroundColor, persons, linkUrl, linkLabel, linkNewTab } =
    props.data as ContactsData

  return (
    <Section background={bgMap[backgroundColor]}>
      <Container className="gap-y-6 md:gap-y-12">
        {heading && (
          <h2 className="col-span-full text-3xl leading-tight text-blue-900 md:text-6xl md:leading-snug">
            {heading}
          </h2>
        )}

        {persons.map((person) => (
          <article
            key={person.id}
            className="col-span-full flex gap-6 md:col-span-6 md:gap-8"
          >
            {person.image ? (
              <div className="size-40 shrink-0 overflow-hidden rounded-xl md:size-44">
                <ResponsiveImage
                  data={person.image}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </div>
            ) : (
              <div className="size-40 shrink-0 rounded-xl bg-slate-300 md:size-44" />
            )}

            <div className="flex min-w-0 flex-col gap-6">
              <div className="flex flex-col gap-2">
                <h3 className="text-2xl font-bold leading-snug text-blue-900 md:text-3xl">
                  {person.name}
                </h3>
                {person.jobTitle && (
                  <p className="text-base leading-normal text-slate-900">
                    {person.jobTitle}
                  </p>
                )}
              </div>

              {(person.email || person.phone) && (
                <address className="flex flex-col not-italic">
                  {person.email && (
                    <a
                      href={`mailto:${person.email}`}
                      className="break-all text-base line-clamp-1 truncate leading-normal text-slate-900 underline decoration-blue-500"
                    >
                      <span className="sr-only">E-post: </span>
                      {person.email}
                    </a>
                  )}
                  {person.phone && (
                    <a
                      href={`tel:${person.phone.replace(/\s/g, '')}`}
                      className="text-base leading-normal text-slate-900 underline decoration-blue-500"
                    >
                      <span className="sr-only">Telefon: </span>
                      {person.phone}
                    </a>
                  )}
                </address>
              )}
            </div>
          </article>
        ))}

        {linkUrl && linkLabel && (
          <div className="col-span-full">
            <a
              href={linkUrl}
              className="text-base leading-normal text-black underline decoration-slate-900 focus-visible:outline-1 focus-visible:outline-slate-300"
              {...(linkNewTab
                ? { target: '_blank', rel: 'noopener noreferrer' }
                : {})}
            >
              {linkLabel}{' '}
              <Icon name="arrow_forward" size={18} className="align-middle" />
            </a>
          </div>
        )}
      </Container>
    </Section>
  )
}
